import { zodResolver } from '@hookform/resolvers/zod'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { authApi } from '@/features/auth/api/authApi'
import { forgotPasswordSchema, type ForgotPasswordFormValues } from '@/features/auth/schemas'
import { paths } from '@/routes/paths'

// PC-FINAL-3: pantalla mínima de "olvidé mi contraseña" -- el backend
// (AuthService.request_password_reset) ya responde con el MISMO mensaje
// exista o no la cuenta, así que esta pantalla nunca puede, por
// construcción, revelar si un correo está registrado (sin enumeración).
export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setFormError(null)
    try {
      await authApi.requestPasswordReset(values.email)
    } catch {
      // Intencional: incluso si la solicitud de red falla, no se revela
      // detalle técnico -- el mismo mensaje neutro de éxito se muestra
      // igual (el usuario puede reintentar si en verdad no le llegó nada).
    }
    setSubmitted(true)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <Stack spacing={0.75} sx={{ mb: 4 }}>
        <Typography variant="h3">Recuperar contraseña</Typography>
        <Typography variant="body2" color="text.secondary">
          Ingresa tu correo institucional y te enviaremos instrucciones para
          restablecer tu contraseña.
        </Typography>
      </Stack>

      {submitted ? (
        <Stack spacing={3}>
          <Alert severity="success">
            Si el correo existe en GOVIA, se enviaron instrucciones de
            restablecimiento. Revisa tu bandeja de entrada.
          </Alert>
          <MuiLink component={RouterLink} to={paths.login} variant="body2">
            Volver a iniciar sesión
          </MuiLink>
        </Stack>
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2.5}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Correo institucional"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Button type="submit" variant="contained" size="large" loading={isSubmitting} fullWidth>
              Enviar instrucciones
            </Button>

            <MuiLink component={RouterLink} to={paths.login} variant="body2" sx={{ alignSelf: 'center' }}>
              Volver a iniciar sesión
            </MuiLink>
          </Stack>
        </Box>
      )}
    </motion.div>
  )
}
